import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { adminApi } from '../../api/clients';

const KIND_LABELS = { hcc: 'Health Care Card', medicare: 'Medicare card' };

/**
 * One uploaded card, fetched with the reviewer's own sign-in.
 *
 * An <img src> cannot carry the staff token, so the file is fetched and shown from memory. Cards
 * are private, so the object URL is released as soon as the dialog closes.
 */
const ReviewImage = ({ image }) => {
    const [objectUrl, setObjectUrl] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!image.viewable) return undefined;

        let created = '';
        let ignore = false;
        setFailed(false);

        adminApi
            .get('/reviewImage', { params: { path: image.object_path }, responseType: 'blob' })
            .then(({ data }) => {
                if (ignore) return;
                created = URL.createObjectURL(data);
                setObjectUrl(created);
            })
            .catch(() => {
                if (!ignore) setFailed(true);
            });

        return () => {
            ignore = true;
            if (created) URL.revokeObjectURL(created);
        };
    }, [image.object_path, image.viewable]);

    const label = KIND_LABELS[image.kind] || 'Card';
    const caption = `${label}${image.student_id ? ` · student ${image.student_id}` : ' · family'}`;

    return (
        <Box sx={{ width: 200 }}>
            {objectUrl ? (
                <a href={objectUrl} target="_blank" rel="noopener noreferrer">
                    <Box
                        component="img"
                        src={objectUrl}
                        alt={`${label}${image.student_id ? ` for student ${image.student_id}` : ''}`}
                        sx={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'block' }}
                    />
                </a>
            ) : (
                <Box
                    sx={{
                        p: 2,
                        minHeight: 96,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        border: '1px dashed #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#64748b',
                    }}
                >
                    {image.viewable && !failed ? (
                        <CircularProgress size={16} />
                    ) : (
                        <span>
                            {image.unavailable === 'legacy'
                                ? 'Uploaded to the previous system, so it is not stored here. The file is on the school’s records drive.'
                                : 'This file could not be opened. It may have been removed.'}
                        </span>
                    )}
                </Box>
            )}

            <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.5 }}>{caption}</Typography>
            {image.file_name && (
                <Typography sx={{ fontSize: '11px', color: '#94a3b8', wordBreak: 'break-all' }}>
                    {image.file_name}
                </Typography>
            )}
        </Box>
    );
};

export default ReviewImage;
